import { c as _c } from "react/compiler-runtime";
import { Locale } from "@/types/locale";
import { ConcreteEntityRightType } from "@/types/rights";
import { InvariantValue } from "@/types/squidex";
import { IconCheck } from "@tabler/icons-react";
import React, { createContext, FC, PropsWithChildren, useCallback, useContext, useMemo, useRef, useState } from "react";
import { FieldValues, useFormContext, useWatch } from "react-hook-form";
import { useLocale, useTranslations } from "next-intl";
import { SwitchLocaleToggle } from "@components/common/buttons/switch-locale-toggle";
import { ContainerWithHeaderSimple } from "@components/common/containers/container-with-header";
import { ContentViewBlock } from "@components/common/content/content-view-block";
import { FormFieldAbstract } from "@components/common/forms/form-field-abstract";
import { FormTextField } from "@components/common/forms/form-text-field";
import { FormWideLocalizedField } from "@components/common/forms/form-wide-localized-field";
import { Badge } from "@components/ui/badge";
import { Button } from "@components/ui/button";
import { FieldDescription, FieldGroup, FieldLegend, FieldSeparator, FieldSet } from "@components/ui/field";
import { Input } from "@components/ui/input";
import { Spinner } from "@components/ui/spinner";
import { localizeFrom } from "@lib/i18n/utils";
import { cn } from "@lib/utils";
import { replaceTwitterContact, resolveBiographyLocales } from "@features/proposals/member-biography-tools";
import { useTranslateProposalFields } from "@features/proposals/hooks/proposal-translate-fields";
import { proposalsTools } from "@features/proposals/proposals-tools";
import { ContributorBiographyInlineOutputView } from "@features/proposals/types/member";
import { MemberBiographyViewBlock } from "@features/proposals/ui/member/member-biography-view-block";
import { MemberPhotoUpload } from "@features/proposals/ui/member/member-photo-upload";
const CONTACTS_FIELD_PATH = "eventProfile.biography.content.data.contacts.iv";
export interface MemberBiographyEditProviderProps {
  readonly biography?: ContributorBiographyInlineOutputView;
  readonly biographies?: readonly ContributorBiographyInlineOutputView[];
  readonly biographiesLoading: boolean;
  readonly biographiesError: boolean;
  readonly patchRight?: ConcreteEntityRightType;
  readonly canUploadPhoto: boolean;
  readonly isMe?: boolean;
  readonly isOpen: boolean;
  readonly isPending?: boolean;
  readonly requiresProfileBeforePhoto?: boolean;
  readonly onRetryBiographies?: () => unknown;
  readonly onReuseBiography: (biography: ContributorBiographyInlineOutputView) => Promise<FieldValues | undefined>;
  readonly onUploadPhoto: (body: FormData, formValues: FieldValues) => Promise<void>;
}
interface MemberBiographyEditContextValue extends MemberBiographyEditProviderProps {
  readonly availableLocales: Locale[];
  readonly firstName?: string;
  readonly lastName?: string;
  readonly historyAvailable: boolean;
  readonly locale: Locale | "all";
  readonly otherBiographies: readonly ContributorBiographyInlineOutputView[];
  readonly showBiographyHistory: boolean;
  readonly isBiographyExpanded: (biographyKey: string) => boolean;
  readonly isBiographyReusePending: (biographyKey: string) => boolean;
  readonly onExpandBiography: (biographyKey: string) => void;
  readonly onCloseBiographyHistory: () => void;
  readonly onLocaleChange: React.Dispatch<React.SetStateAction<Locale | "all">>;
  readonly onReuseBiographySafely: (biography: ContributorBiographyInlineOutputView, biographyKey: string) => Promise<FieldValues | undefined>;
  readonly onRetryBiographiesSafely: () => void;
  readonly onToggleBiographyHistory: () => void;
  readonly onUploadPhotoSafely: (body: FormData, formValues: FieldValues) => Promise<void>;
}
const MemberBiographyEditContext = createContext<MemberBiographyEditContextValue | undefined>(undefined);
const useMemberBiographyEditContext = () => {
  const context = useContext(MemberBiographyEditContext);
  if (!context) {
    throw new Error("Member biography edit components must be used inside their provider");
  }
  return context;
};
export const MemberBiographyEditProvider: FC<PropsWithChildren<MemberBiographyEditProviderProps>> = t0 => {
  const $ = _c(58);
  let biographies;
  let biography;
  let children;
  let isOpen;
  let onRetryBiographies;
  let onReuseBiography;
  let onUploadPhoto;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      biographies,
      biography,
      children,
      isOpen,
      isPending: t1,
      onRetryBiographies,
      onReuseBiography,
      onUploadPhoto,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = biographies;
    $[2] = biography;
    $[3] = children;
    $[4] = isOpen;
    $[5] = onRetryBiographies;
    $[6] = onReuseBiography;
    $[7] = onUploadPhoto;
    $[8] = props;
    $[9] = t1;
  } else {
    biographies = $[1];
    biography = $[2];
    children = $[3];
    isOpen = $[4];
    onRetryBiographies = $[5];
    onReuseBiography = $[6];
    onUploadPhoto = $[7];
    props = $[8];
    t1 = $[9];
  }
  const isPending = t1 === undefined ? false : t1;
  const systemLocale = useLocale() as Locale;
  const [locale, setLocale] = useState("all");
  const [showBiographyHistory, setShowBiographyHistory] = useState(false);
  const [expandedBiographyKeys, setExpandedBiographyKeys] = useState(_temp);
  const [pendingBiographyKeys, setPendingBiographyKeys] = useState(_temp2);
  const reusePromiseRef = useRef(null);
  biography?.content?.id;
  let t2;
  if ($[10] !== biographies || $[11] !== biography?.content?.id) {
    t2 = biographies?.filter(previousBiography => previousBiography.content?.id !== biography?.content?.id) ?? [];
    $[10] = biographies;
    $[11] = biography?.content?.id;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  const otherBiographies = t2;
  let t3;
  if ($[13] !== biography) {
    t3 = resolveBiographyLocales(biography);
    $[13] = biography;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  const availableLocales = t3;
  const t4 = biography?.content?.data?.firstName;
  let t5;
  if ($[15] !== systemLocale || $[16] !== t4) {
    t5 = localizeFrom(t4, systemLocale);
    $[15] = systemLocale;
    $[16] = t4;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  const firstName = t5;
  const t6 = biography?.content?.data?.lastName;
  let t7;
  if ($[18] !== systemLocale || $[19] !== t6) {
    t7 = localizeFrom(t6, systemLocale);
    $[18] = systemLocale;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  const lastName = t7;
  const historyAvailable = isOpen && onRetryBiographies !== undefined;
  const isOperationPending = isPending || pendingBiographyKeys.size > 0;
  let t8;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = biographyKey => {
      setExpandedBiographyKeys(current => new Set(current).add(biographyKey));
    };
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const onExpandBiography = t8;
  let t9;
  if ($[22] !== onReuseBiography) {
    t9 = (previousBiography_0, biographyKey_0) => {
      if (reusePromiseRef.current) {
        return reusePromiseRef.current;
      }
      setPendingBiographyKeys(current_0 => new Set(current_0).add(biographyKey_0));
      const operation = Promise.resolve().then(() => onReuseBiography(previousBiography_0)).catch(_temp3).finally(() => {
        setPendingBiographyKeys(current_1 => {
          const next = new Set(current_1);
          next.delete(biographyKey_0);
          return next;
        });
        if (reusePromiseRef.current === operation) {
          reusePromiseRef.current = null;
        }
      });
      reusePromiseRef.current = operation;
      return operation;
    };
    $[22] = onReuseBiography;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const onReuseBiographySafely = t9;
  let t10;
  if ($[24] !== onRetryBiographies) {
    t10 = () => {
      Promise.resolve(onRetryBiographies?.()).catch(_temp4);
    };
    $[24] = onRetryBiographies;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  const onRetryBiographiesSafely = t10;
  let t11;
  if ($[26] !== onUploadPhoto) {
    t11 = async (body, formValues) => {
      try {
        await onUploadPhoto(body, formValues);
      } catch {}
    };
    $[26] = onUploadPhoto;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  const onUploadPhotoSafely = t11;
  let t12;
  if ($[28] !== expandedBiographyKeys) {
    t12 = biographyKey_1 => expandedBiographyKeys.has(biographyKey_1);
    $[28] = expandedBiographyKeys;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== pendingBiographyKeys) {
    t13 = biographyKey_2 => pendingBiographyKeys.has(biographyKey_2);
    $[30] = pendingBiographyKeys;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => setShowBiographyHistory(false);
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = () => setShowBiographyHistory(_temp5);
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== availableLocales || $[35] !== biographies || $[36] !== biography || $[37] !== firstName || $[38] !== historyAvailable || $[39] !== isOpen || $[40] !== isOperationPending || $[41] !== lastName || $[42] !== locale || $[43] !== onRetryBiographies || $[44] !== onRetryBiographiesSafely || $[45] !== onReuseBiography || $[46] !== onReuseBiographySafely || $[47] !== onUploadPhoto || $[48] !== onUploadPhotoSafely || $[49] !== otherBiographies || $[50] !== props || $[51] !== showBiographyHistory || $[52] !== t12 || $[53] !== t13) {
    t16 = {
      ...props,
      availableLocales,
      biographies,
      biography,
      firstName,
      historyAvailable,
      isPending: isOperationPending,
      isBiographyExpanded: t12,
      isBiographyReusePending: t13,
      isOpen,
      lastName,
      locale,
      onExpandBiography,
      onCloseBiographyHistory: t14,
      onLocaleChange: setLocale,
      onRetryBiographies,
      onRetryBiographiesSafely,
      onReuseBiography,
      onReuseBiographySafely,
      onToggleBiographyHistory: t15,
      onUploadPhoto,
      onUploadPhotoSafely,
      otherBiographies,
      showBiographyHistory
    };
    $[34] = availableLocales;
    $[35] = biographies;
    $[36] = biography;
    $[37] = firstName;
    $[38] = historyAvailable;
    $[39] = isOpen;
    $[40] = isOperationPending;
    $[41] = lastName;
    $[42] = locale;
    $[43] = onRetryBiographies;
    $[44] = onRetryBiographiesSafely;
    $[45] = onReuseBiography;
    $[46] = onReuseBiographySafely;
    $[47] = onUploadPhoto;
    $[48] = onUploadPhotoSafely;
    $[49] = otherBiographies;
    $[50] = props;
    $[51] = showBiographyHistory;
    $[52] = t12;
    $[53] = t13;
    $[54] = t16;
  } else {
    t16 = $[54];
  }
  const value = t16;
  let t17;
  if ($[55] !== children || $[56] !== value) {
    t17 = <MemberBiographyEditContext.Provider value={value}>{children}</MemberBiographyEditContext.Provider>;
    $[55] = children;
    $[56] = value;
    $[57] = t17;
  } else {
    t17 = $[57];
  }
  return t17;
};
export const MemberBiographyEditActions: FC = () => {
  const $ = _c(29);
  const t = useTranslations("features.proposals.ui.memberContentEditBlock");
  const {
    availableLocales,
    biographiesError,
    biographiesLoading,
    historyAvailable,
    isPending,
    locale,
    onLocaleChange,
    onRetryBiographiesSafely,
    onToggleBiographyHistory,
    otherBiographies,
    showBiographyHistory
  } = useMemberBiographyEditContext();
  let t0;
  if ($[0] !== availableLocales || $[1] !== locale || $[2] !== onLocaleChange || $[3] !== showBiographyHistory) {
    t0 = !showBiographyHistory && <SwitchLocaleToggle locale={locale as Locale} setLocale={onLocaleChange} locales={availableLocales} enableAllLocales={true} buttonClassName="last:rounded-r-none" />;
    $[0] = availableLocales;
    $[1] = locale;
    $[2] = onLocaleChange;
    $[3] = showBiographyHistory;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  let t1;
  if ($[5] !== biographiesLoading || $[6] !== historyAvailable || $[7] !== t) {
    t1 = historyAvailable && biographiesLoading && <span className="flex items-center gap-2 text-sm text-muted-foreground" aria-live="polite"><Spinner />{t("biography.historyLoading")}</span>;
    $[5] = biographiesLoading;
    $[6] = historyAvailable;
    $[7] = t;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  let t2;
  if ($[9] !== biographiesError || $[10] !== historyAvailable || $[11] !== isPending || $[12] !== onRetryBiographiesSafely || $[13] !== t) {
    t2 = historyAvailable && biographiesError && <div className="flex items-center gap-2 text-sm text-destructive" role="alert"><span>{t("biography.historyLoadError")}</span><Button variant="outline" size="sm" disabled={isPending} onClick={onRetryBiographiesSafely}>{t("biography.retryHistory")}</Button></div>;
    $[9] = biographiesError;
    $[10] = historyAvailable;
    $[11] = isPending;
    $[12] = onRetryBiographiesSafely;
    $[13] = t;
    $[14] = t2;
  } else {
    t2 = $[14];
  }
  let t3;
  if ($[15] !== biographiesError || $[16] !== biographiesLoading || $[17] !== historyAvailable || $[18] !== isPending || $[19] !== onToggleBiographyHistory || $[20] !== otherBiographies || $[21] !== showBiographyHistory || $[22] !== t) {
    t3 = historyAvailable && !biographiesLoading && !biographiesError && <Button variant="outline" size="sm" disabled={isPending} onClick={onToggleBiographyHistory}>{showBiographyHistory ? t("biography.hideVersions") : t("biography.viewVersions", {
        count: otherBiographies.length
      })}</Button>;
    $[15] = biographiesError;
    $[16] = biographiesLoading;
    $[17] = historyAvailable;
    $[18] = isPending;
    $[19] = onToggleBiographyHistory;
    $[20] = otherBiographies;
    $[21] = showBiographyHistory;
    $[22] = t;
    $[23] = t3;
  } else {
    t3 = $[23];
  }
  let t4;
  if ($[24] !== t0 || $[25] !== t1 || $[26] !== t2 || $[27] !== t3) {
    t4 = <>{t0}{t1}{t2}{t3}</>;
    $[24] = t0;
    $[25] = t1;
    $[26] = t2;
    $[27] = t3;
    $[28] = t4;
  } else {
    t4 = $[28];
  }
  return t4;
};
const TwitterField = () => {
  const $ = _c(16);
  const form = useFormContext();
  const {
    translateFieldDescription,
    translateFieldLabel
  } = useTranslateProposalFields().activityMember.biography;
  let t0;
  if ($[0] !== form.control) {
    t0 = {
      control: form.control,
      name: CONTACTS_FIELD_PATH
    };
    $[0] = form.control;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const contacts = useWatch(t0) as InvariantValue[] | undefined;
  let t1;
  if ($[2] !== contacts) {
    t1 = contacts?.find(_temp6)?.value ?? "";
    $[2] = contacts;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const twitter = t1;
  const {
    patchRight
  } = useMemberBiographyEditContext();
  let t2;
  if ($[4] !== translateFieldLabel) {
    t2 = translateFieldLabel("twitter");
    $[4] = translateFieldLabel;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== translateFieldDescription) {
    t3 = translateFieldDescription("twitter");
    $[6] = translateFieldDescription;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== contacts || $[9] !== twitter) {
    t4 = (field, isDirty) => <Input name={field.name} ref={field.ref} onBlur={field.onBlur} value={twitter} disabled={field.disabled} onChange={event => field.onChange(replaceTwitterContact(contacts, event.target.value))} className={cn({
      "bg-gray-200 dark:bg-gray-700": isDirty
    })} />;
    $[8] = contacts;
    $[9] = twitter;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== patchRight || $[12] !== t2 || $[13] !== t3 || $[14] !== t4) {
    t5 = <FormFieldAbstract name={CONTACTS_FIELD_PATH} rights={patchRight} label={t2} description={t3} renderControl={t4} />;
    $[11] = patchRight;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  return t5;
};
const MemberBiographyHistory = () => {
  const $ = _c(32);
  const systemLocale = useLocale() as Locale;
  const t = useTranslations("features.proposals.ui.memberContentEditBlock");
  const {
    reset
  } = useFormContext();
  const {
    isBiographyExpanded,
    isBiographyReusePending,
    isPending,
    onExpandBiography,
    onCloseBiographyHistory,
    onReuseBiographySafely,
    otherBiographies,
    patchRight
  } = useMemberBiographyEditContext();
  let t0;
  if ($[0] !== t) {
    t0 = t("biography.previousVersions");
    $[0] = t;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== otherBiographies.length || $[3] !== t) {
    t1 = otherBiographies.length === 0 && <p className="text-sm text-muted-foreground">{t("biography.emptyHistory")}</p>;
    $[2] = otherBiographies.length;
    $[3] = t;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== isBiographyExpanded || $[6] !== isBiographyReusePending || $[7] !== isPending || $[8] !== onCloseBiographyHistory || $[9] !== onExpandBiography || $[10] !== onReuseBiographySafely || $[11] !== otherBiographies || $[12] !== patchRight || $[13] !== reset || $[14] !== systemLocale || $[15] !== t) {
    let t3;
    if ($[17] !== isBiographyExpanded || $[18] !== isBiographyReusePending || $[19] !== isPending || $[20] !== onCloseBiographyHistory || $[21] !== onExpandBiography || $[22] !== onReuseBiographySafely || $[23] !== patchRight || $[24] !== reset || $[25] !== systemLocale || $[26] !== t) {
      t3 = (previousBiography, index) => {
        const biographyKey = previousBiography.content?.id ?? `biography-${index}`;
        const events = proposalsTools.activityMemberTools.getEventsUsedInFromBio(previousBiography);
        const otherEvents = events?.slice(1);
        const isExpanded = isBiographyExpanded(biographyKey);
        const isPatching = isBiographyReusePending(biographyKey);
        return <ContentViewBlock key={biographyKey} title={events?.[0]?.objectName ?? ""} descriptionNode={otherEvents && otherEvents.length > 0 && <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground"><span>{t("biography.usedInEvents")}</span><Badge variant="secondary" className="text-xs">{otherEvents[0].objectName}</Badge>{otherEvents.length > 1 && !isExpanded && <Button type="button" variant="outline" size="sm" aria-expanded={isExpanded} disabled={isPending} className="h-5 rounded-full px-2 py-0 text-xs font-normal" onClick={() => onExpandBiography(biographyKey)}>{t("biography.andMore", {
              count: otherEvents.length - 1
            })}</Button>}{isExpanded && otherEvents.slice(1).map(_temp7)}</div>} actions={<Button variant="outline" size="sm" disabled={!patchRight || isPending || isPatching} className="ml-auto" onClick={() => void onReuseBiographySafely(previousBiography, biographyKey).then(updatedEntity => {
          if (updatedEntity !== undefined) {
            reset(updatedEntity);
            onCloseBiographyHistory();
          }
        })}>{isPatching && <Spinner />}{!isPatching && <IconCheck />}{t("biography.useVersion")}</Button>}><MemberBiographyViewBlock biography={previousBiography} locale={systemLocale} /></ContentViewBlock>;
      };
      $[17] = isBiographyExpanded;
      $[18] = isBiographyReusePending;
      $[19] = isPending;
      $[20] = onCloseBiographyHistory;
      $[21] = onExpandBiography;
      $[22] = onReuseBiographySafely;
      $[23] = patchRight;
      $[24] = reset;
      $[25] = systemLocale;
      $[26] = t;
      $[27] = t3;
    } else {
      t3 = $[27];
    }
    t2 = otherBiographies.map(t3);
    $[5] = isBiographyExpanded;
    $[6] = isBiographyReusePending;
    $[7] = isPending;
    $[8] = onCloseBiographyHistory;
    $[9] = onExpandBiography;
    $[10] = onReuseBiographySafely;
    $[11] = otherBiographies;
    $[12] = patchRight;
    $[13] = reset;
    $[14] = systemLocale;
    $[15] = t;
    $[16] = t2;
  } else {
    t2 = $[16];
  }
  let t3;
  if ($[28] !== t0 || $[29] !== t1 || $[30] !== t2) {
    t3 = <ContainerWithHeaderSimple contentClassName="grid gap-6 spaced-y-6" headerLabel={t0}>{t1}{t2}</ContainerWithHeaderSimple>;
    $[28] = t0;
    $[29] = t1;
    $[30] = t2;
    $[31] = t3;
  } else {
    t3 = $[31];
  }
  return t3;
};
const MemberBiographyCurrentForm = () => {
  const $ = _c(99);
  const t = useTranslations("features.proposals.ui.memberContentEditBlock");
  const form = useFormContext();
  const {
    biography,
    canUploadPhoto,
    firstName,
    isMe,
    isPending,
    lastName,
    locale,
    onUploadPhotoSafely,
    patchRight,
    requiresProfileBeforePhoto: t0
  } = useMemberBiographyEditContext();
  const requiresProfileBeforePhoto = t0 === undefined ? false : t0;
  const {
    translateFieldDescription,
    translateFieldLabel,
    translateFieldMetadata,
    translateFieldPlaceholder
  } = useTranslateProposalFields().activityMember.biography;
  let t1;
  if ($[0] !== form || $[1] !== onUploadPhotoSafely || $[2] !== requiresProfileBeforePhoto) {
    t1 = body => {
      if (!requiresProfileBeforePhoto) {
        return onUploadPhotoSafely(body, form.getValues());
      }
      return form.handleSubmit(formValues => onUploadPhotoSafely(body, formValues), _temp8)();
    };
    $[0] = form;
    $[1] = onUploadPhotoSafely;
    $[2] = requiresProfileBeforePhoto;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const uploadPhoto = t1;
  let t2;
  if ($[4] !== t) {
    t2 = t("biography.currentVersion");
    $[4] = t;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const t3 = !canUploadPhoto || isPending;
  let t4;
  if ($[6] !== biography || $[7] !== firstName || $[8] !== isMe || $[9] !== lastName || $[10] !== t3 || $[11] !== uploadPhoto) {
    t4 = <div className="rounded-md border p-4"><MemberPhotoUpload biography={biography} firstName={firstName} lastName={lastName} disabled={t3} onPhotoSelect={uploadPhoto} className="pl-2" isMe={isMe} /></div>;
    $[6] = biography;
    $[7] = firstName;
    $[8] = isMe;
    $[9] = lastName;
    $[10] = t3;
    $[11] = uploadPhoto;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== t) {
    t5 = t("biography.form.generalInfo.label");
    $[13] = t;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== t5) {
    t6 = <FieldLegend>{t5}</FieldLegend>;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== t) {
    t7 = t("biography.form.generalInfo.description");
    $[17] = t;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== t7) {
    t8 = <FieldDescription>{t7}</FieldDescription>;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const t9 = locale === "all" ? "grid grid-cols-1 gap-6" : "grid grid-cols-2 gap-4";
  let t10;
  if ($[21] !== t9) {
    t10 = cn("px-4", t9);
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== locale || $[24] !== patchRight || $[25] !== translateFieldDescription || $[26] !== translateFieldLabel || $[27] !== translateFieldPlaceholder) {
    t11 = locale === "all" ? <><FormWideLocalizedField name="eventProfile.biography.content.data.firstName" rights={patchRight} label={translateFieldLabel("firstName")} placeholder={translateFieldPlaceholder("firstName")} required={true} size="xs" /><FormWideLocalizedField name="eventProfile.biography.content.data.lastName" rights={patchRight} label={translateFieldLabel("lastName")} placeholder={translateFieldPlaceholder("lastName")} required={true} size="xs" /><FormWideLocalizedField name="eventProfile.biography.content.data.alias" rights={patchRight} label={translateFieldLabel("alias")} placeholder={translateFieldPlaceholder("alias")} description={translateFieldDescription("alias")} size="xs" /></> : <React.Fragment key={`name-fields-${locale}`}><FormTextField name={`eventProfile.biography.content.data.firstName.${locale}`} rights={patchRight} label={translateFieldLabel("firstName")} placeholder={translateFieldPlaceholder("firstName")} required={true} /><FormTextField name={`eventProfile.biography.content.data.lastName.${locale}`} rights={patchRight} label={translateFieldLabel("lastName")} placeholder={translateFieldPlaceholder("lastName")} required={true} /><FormTextField name={`eventProfile.biography.content.data.alias.${locale}`} rights={patchRight} label={translateFieldLabel("alias")} placeholder={translateFieldPlaceholder("alias")} description={translateFieldDescription("alias")} /></React.Fragment>;
    $[23] = locale;
    $[24] = patchRight;
    $[25] = translateFieldDescription;
    $[26] = translateFieldLabel;
    $[27] = translateFieldPlaceholder;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== t10 || $[30] !== t11) {
    t12 = <FieldGroup className={t10}>{t11}</FieldGroup>;
    $[29] = t10;
    $[30] = t11;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <FieldSeparator />;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== t12 || $[34] !== t6 || $[35] !== t8) {
    t14 = <FieldSet>{t6}{t8}{t12}{t13}</FieldSet>;
    $[33] = t12;
    $[34] = t6;
    $[35] = t8;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  let t15;
  if ($[37] !== t) {
    t15 = t("biography.form.company.label");
    $[37] = t;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] !== t15) {
    t16 = <FieldLegend>{t15}</FieldLegend>;
    $[39] = t15;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  let t17;
  if ($[41] !== t) {
    t17 = t("biography.form.company.description");
    $[41] = t;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  let t18;
  if ($[43] !== t17) {
    t18 = <FieldDescription>{t17}</FieldDescription>;
    $[43] = t17;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  const t19 = locale === "all" ? "grid grid-cols-1 gap-6" : "grid grid-cols-2 gap-4";
  let t20;
  if ($[45] !== t19) {
    t20 = cn("px-4", t19);
    $[45] = t19;
    $[46] = t20;
  } else {
    t20 = $[46];
  }
  let t21;
  if ($[47] !== locale || $[48] !== patchRight || $[49] !== translateFieldDescription || $[50] !== translateFieldLabel || $[51] !== translateFieldMetadata || $[52] !== translateFieldPlaceholder) {
    t21 = locale === "all" ? <><FormWideLocalizedField name="eventProfile.biography.content.data.company" rights={patchRight} label={translateFieldLabel("company")} description={translateFieldMetadata("company", "notes")} placeholder={translateFieldPlaceholder("company")} /><FormWideLocalizedField name="eventProfile.biography.content.data.position" rights={patchRight} label={translateFieldLabel("position")} description={translateFieldDescription("position")} placeholder={translateFieldPlaceholder("position")} /></> : <React.Fragment key={`company-fields-${locale}`}><FormTextField name={`eventProfile.biography.content.data.company.${locale}`} rights={patchRight} label={translateFieldLabel("company")} description={translateFieldMetadata("company", "notes")} placeholder={translateFieldPlaceholder("company")} /><FormTextField name={`eventProfile.biography.content.data.position.${locale}`} rights={patchRight} label={translateFieldLabel("position")} description={translateFieldDescription("position")} placeholder={translateFieldPlaceholder("position")} /></React.Fragment>;
    $[47] = locale;
    $[48] = patchRight;
    $[49] = translateFieldDescription;
    $[50] = translateFieldLabel;
    $[51] = translateFieldMetadata;
    $[52] = translateFieldPlaceholder;
    $[53] = t21;
  } else {
    t21 = $[53];
  }
  let t22;
  if ($[54] !== t20 || $[55] !== t21) {
    t22 = <FieldGroup className={t20}>{t21}</FieldGroup>;
    $[54] = t20;
    $[55] = t21;
    $[56] = t22;
  } else {
    t22 = $[56];
  }
  let t23;
  if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <FieldSeparator />;
    $[57] = t23;
  } else {
    t23 = $[57];
  }
  let t24;
  if ($[58] !== t16 || $[59] !== t18 || $[60] !== t22) {
    t24 = <FieldSet>{t16}{t18}{t22}{t23}</FieldSet>;
    $[58] = t16;
    $[59] = t18;
    $[60] = t22;
    $[61] = t24;
  } else {
    t24 = $[61];
  }
  let t25;
  if ($[62] !== t) {
    t25 = t("biography.form.aboutInfo.label");
    $[62] = t;
    $[63] = t25;
  } else {
    t25 = $[63];
  }
  let t26;
  if ($[64] !== t25) {
    t26 = <FieldLegend>{t25}</FieldLegend>;
    $[64] = t25;
    $[65] = t26;
  } else {
    t26 = $[65];
  }
  let t27;
  if ($[66] !== t) {
    t27 = t("biography.form.aboutInfo.description");
    $[66] = t;
    $[67] = t27;
  } else {
    t27 = $[67];
  }
  let t28;
  if ($[68] !== t27) {
    t28 = <FieldDescription>{t27}</FieldDescription>;
    $[68] = t27;
    $[69] = t28;
  } else {
    t28 = $[69];
  }
  const t29 = locale === "all" && "gap-6";
  let t30;
  if ($[70] !== t29) {
    t30 = cn("px-4", t29);
    $[70] = t29;
    $[71] = t30;
  } else {
    t30 = $[71];
  }
  let t31;
  if ($[72] !== translateFieldLabel) {
    t31 = translateFieldLabel("titulus");
    $[72] = translateFieldLabel;
    $[73] = t31;
  } else {
    t31 = $[73];
  }
  let t32;
  if ($[74] !== translateFieldDescription) {
    t32 = translateFieldDescription("titulus");
    $[74] = translateFieldDescription;
    $[75] = t32;
  } else {
    t32 = $[75];
  }
  let t33;
  if ($[76] !== patchRight || $[77] !== t31 || $[78] !== t32) {
    t33 = <FormTextField name="eventProfile.biography.content.data.titulus.iv" rights={patchRight} label={t31} description={t32} placeholder="Java Champion, MVP, GDE..." className="max-w-1/3" />;
    $[76] = patchRight;
    $[77] = t31;
    $[78] = t32;
    $[79] = t33;
  } else {
    t33 = $[79];
  }
  let t34;
  if ($[80] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <TwitterField />;
    $[80] = t34;
  } else {
    t34 = $[80];
  }
  let t35;
  if ($[81] !== locale || $[82] !== patchRight || $[83] !== translateFieldLabel) {
    t35 = locale === "all" ? <FormWideLocalizedField name="eventProfile.biography.content.data.description" rights={patchRight} label={translateFieldLabel("description")} requiredLocales={["ru"]} richEditor={true} orientation="vertical" /> : <FormTextField key={`description.${locale}`} name={`eventProfile.biography.content.data.description.${locale}`} rights={patchRight} label={translateFieldLabel("description")} required={locale === "ru"} richEditor={true} />;
    $[81] = locale;
    $[82] = patchRight;
    $[83] = translateFieldLabel;
    $[84] = t35;
  } else {
    t35 = $[84];
  }
  let t36;
  if ($[85] !== t30 || $[86] !== t33 || $[87] !== t35) {
    t36 = <FieldGroup className={t30}>{t33}{t34}{t35}</FieldGroup>;
    $[85] = t30;
    $[86] = t33;
    $[87] = t35;
    $[88] = t36;
  } else {
    t36 = $[88];
  }
  let t37;
  if ($[89] !== t26 || $[90] !== t28 || $[91] !== t36) {
    t37 = <FieldSet>{t26}{t28}{t36}</FieldSet>;
    $[89] = t26;
    $[90] = t28;
    $[91] = t36;
    $[92] = t37;
  } else {
    t37 = $[92];
  }
  let t38;
  if ($[93] !== t14 || $[94] !== t2 || $[95] !== t24 || $[96] !== t37 || $[97] !== t4) {
    t38 = <ContainerWithHeaderSimple contentClassName="grid gap-6 spaced-y-6" headerLabel={t2}>{t4}{t14}{t24}{t37}</ContainerWithHeaderSimple>;
    $[93] = t14;
    $[94] = t2;
    $[95] = t24;
    $[96] = t37;
    $[97] = t4;
    $[98] = t38;
  } else {
    t38 = $[98];
  }
  return t38;
};
export const MemberBiographyEditContent: FC = () => {
  const $ = _c(3);
  const {
    biographiesError,
    showBiographyHistory
  } = useMemberBiographyEditContext();
  let t0;
  if ($[0] !== biographiesError || $[1] !== showBiographyHistory) {
    t0 = showBiographyHistory && !biographiesError ? <MemberBiographyHistory /> : <MemberBiographyCurrentForm />;
    $[0] = biographiesError;
    $[1] = showBiographyHistory;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
};
function _temp() {
  return new Set();
}
function _temp2() {
  return new Set();
}
function _temp3() {}
function _temp4() {}
function _temp5(current_2) {
  return !current_2;
}
function _temp6(contact) {
  return contact.type === "twitter";
}
function _temp7(event) {
  return <Badge key={event.objectId} variant="secondary" className="text-xs">{event.objectName}</Badge>;
}
function _temp8() {}